import * as Sentry from '@sentry/react'
import Logger, { CloudwatchMetadata } from '~/services/Logger'
import { captureException } from '~/services/sentry'
import {
  AnalyticsEventFromNamespace,
  AnalyticsNamespace,
} from '~/types/Analytics'
import { Analytics } from '~/utils/analytics'
import { capturePosthogEvent } from '~/utils/initializePosthog'

import { getObservabilitySessionId } from './correlation'

// Exhaustive, correctly-typed instance per namespace: no casts, and adding a
// namespace without an instance is a compile error. Construction is trivial
// (each instance only stores its namespace string).
const analyticsInstances: {
  [TNamespace in AnalyticsNamespace]: Analytics<TNamespace>
} = {
  [AnalyticsNamespace.SIP_CLIENT]: new Analytics(AnalyticsNamespace.SIP_CLIENT),
  [AnalyticsNamespace.VOICEMAIL]: new Analytics(AnalyticsNamespace.VOICEMAIL),
  [AnalyticsNamespace.FAXING]: new Analytics(AnalyticsNamespace.FAXING),
  [AnalyticsNamespace.CONTACTS]: new Analytics(AnalyticsNamespace.CONTACTS),
  [AnalyticsNamespace.CALL_ANALYTICS]: new Analytics(
    AnalyticsNamespace.CALL_ANALYTICS,
  ),
  [AnalyticsNamespace.WEB_PHONE]: new Analytics(AnalyticsNamespace.WEB_PHONE),
  [AnalyticsNamespace.MESSAGING]: new Analytics(AnalyticsNamespace.MESSAGING),
  [AnalyticsNamespace.AUTHENTICATION]: new Analytics(
    AnalyticsNamespace.AUTHENTICATION,
  ),
  [AnalyticsNamespace.SHARED]: new Analytics(AnalyticsNamespace.SHARED),
  [AnalyticsNamespace.WEB_SOCKET]: new Analytics(AnalyticsNamespace.WEB_SOCKET),
  [AnalyticsNamespace.MISSED_CALLS]: new Analytics(
    AnalyticsNamespace.MISSED_CALLS,
  ),
  [AnalyticsNamespace.DESKTOP]: new Analytics(AnalyticsNamespace.DESKTOP),
}

/**
 * Shared observability wrapper (observability RFC): one shape over the sinks
 * we already run. PHI redaction and correlation live at the SDK config layer,
 * not here — payloads passed in must still be PHI-clean.
 */
export const observe = {
  error: captureException,

  /**
   * Domain event via PostHog, prefixed by namespace. Events must come from
   * the namespace's registered enum — add new events there rather than
   * passing loose strings.
   */
  track: <TNamespace extends AnalyticsNamespace>(
    namespace: TNamespace,
    event: AnalyticsEventFromNamespace<TNamespace>,
    data?: Record<string, unknown>,
  ): void => {
    analyticsInstances[namespace].track(event, data)
  },

  /** Sentry span that hands over the span. No-ops until tracing is sampled. */
  trace: <T>(name: string, fn: (span: Sentry.Span) => T): T =>
    Sentry.startSpan({ name }, fn),

  /** PostHog event carrying a value, aggregated in Insights — not a real metrics backend. */
  metric: (
    name: string,
    value: number,
    data?: Record<string, unknown>,
  ): void => {
    // `value` last so a stray `data.value` can't clobber the metric's value.
    capturePosthogEvent(name, { ...data, value })
  },

  log: new Logger('app'),

  /** Tagged logger; CloudWatch metadata also ships it to the per-user stream. */
  logger: (tag: string, metadata?: CloudwatchMetadata): Logger =>
    new Logger(tag, metadata),

  /** Correlation id shared across all sinks for this app load. */
  sessionId: getObservabilitySessionId,

  /** Escape hatch to the raw Sentry SDK. */
  sentry: Sentry,
}
